import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Card from 'reactstrap/lib/Card';
import classnames from 'classnames';
import { IncentivesAd } from 'site-modules/shared/components/native-ad/incentives-ad/incentives-ad';
import { BuildPriceAd } from 'site-modules/shared/components/native-ad/buildprice-ad/buildprice-ad';
import { PricingAd } from 'site-modules/shared/components/native-ad/pricing-ad/pricing-ad';
import { get } from 'lodash-es';
import { Styles } from 'site-modules/shared/components/native-ad/buildprice-site-served-ad/buildprice-site-served-ad-styles';
import {
  svgCarTrims,
  svgColorPalette,
  svgPriceTag,
  svgStreeringWheel,
} from 'client/images/inline-svgs/ads/buildprice-ad-icons';
import { getPlural } from 'client/utils/plural';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { AdLabel } from 'site-modules/shared/components/native-ad/native-ad-components/ad-label/ad-label';
import { ConquestRibbon } from 'site-modules/shared/components/native-ad/native-ad-components/conquest-ribbon/conquest-ribbon';

import './multi-cta-ad.scss';

/**
 * Multi-CTA Ad Component for ADS-12450 A/B Test
 * Consolidates site-incentives, site-buildprice, and site-pricing ads into a single component
 */
export function MultiCtaAdFeatures({
  offersHeadline = null,
  jellybeanImage = {
    src: '',
    photoDisclaimer: '',
  },
  buildPriceFeatures = {
    msrpConfig: null,
    trimNames: null,
    vehicleInfo: null,
  },
  ads12450Recipe = '',
  ads12859Recipe = '',
  headline2 = null,
}) {
  if (!offersHeadline || !buildPriceFeatures) return null;
  const renderAtGlanceItem = (icon, text, children, classes) => (
    <li className={classnames('d-flex', classes)} style={Styles.li}>
      {icon}
      <ContentFragment componentToUse="span" classes="text-gray-darker">
        {text}
      </ContentFragment>
      {children}
    </li>
  );
  const { msrpConfig, trimNames, vehicleInfo } = buildPriceFeatures;
  const { extColorCnt, pkgCnt } = get(vehicleInfo, 'submodels[0]', {});
  const trimCnt = trimNames?.length;
  const makeName = vehicleInfo?.makeName;
  const msrp = msrpConfig?.trim();
  const sectionClasses = 'at-a-glance-container d-flex flex-column justify-content-center';
  const sectionStyle = { flex: '1 1 auto' };
  const makeNameFallback = makeName ? ` for your ${makeName}` : '';
  const fallbackHeadingRetention = `Choose the trim, color, options, packages and more${makeNameFallback}`;

  if (!msrp && !extColorCnt && !trimCnt && !pkgCnt) {
    return (
      <section className={sectionClasses} style={sectionStyle}>
        <header className="size-16 mb-1 fw-medium mt-3 px-1">{headline2 || fallbackHeadingRetention}</header>
        <ul style={Styles.ul} className="px-1">
          {!!offersHeadline &&
            renderAtGlanceItem(
              <i className="icon-price-tag text-blue-50 size-16 me-0_5" aria-hidden />,
              `${offersHeadline}`,
              null
            )}
        </ul>
      </section>
    );
  }

  return (
    <section className={sectionClasses} style={sectionStyle}>
      {headline2 && <header className="size-14 fw-medium mt-3 px-1">{headline2}</header>}
      <ul style={Styles.ul} className="px-1 pt-1">
        {(ads12450Recipe === 'chal1' || ads12859Recipe === 'chal1') && (
          <li className="w-50 pos-r float-end mb-0 ps-0_5">
            <img
              className="img-fluid rounded w-100 h-100"
              src={jellybeanImage.src}
              alt=""
              style={{ maxWidth: '300px' }}
            />
            {jellybeanImage.photoDisclaimer && (
              <figcaption className="w-100 text-end size-12 pos-a mb-0_25">{jellybeanImage.photoDisclaimer}</figcaption>
            )}
          </li>
        )}
        {!headline2 && (
          <li className="size-14 pt-3 fw-medium headline" style={Styles.li}>
            At a glance:
          </li>
        )}
        {!!extColorCnt &&
          renderAtGlanceItem(svgColorPalette, `${extColorCnt} ${getPlural('Color', extColorCnt)}`, null)}
        {!!trimCnt && renderAtGlanceItem(svgCarTrims, `${trimCnt} ${getPlural('Trim', trimCnt)}`, null)}
        {!!pkgCnt && renderAtGlanceItem(svgStreeringWheel, `${pkgCnt} ${getPlural('Package', pkgCnt)}`, null)}
        {!!msrp && renderAtGlanceItem(svgPriceTag, msrp, <span className="size-12 ms-0_25">starting MSRP</span>)}
        {!!offersHeadline &&
          renderAtGlanceItem(
            <i className="icon-price-tag text-blue-50 size-16 me-0_5" aria-hidden />,
            `${offersHeadline}`,
            null,
            'align-items-center'
          )}
      </ul>
    </section>
  );
}
MultiCtaAdFeatures.propTypes = {
  offersHeadline: PropTypes.string,
  headline2: PropTypes.string,
  jellybeanImage: PropTypes.shape({
    src: PropTypes.string,
    photoDisclaimer: PropTypes.string,
  }),
  buildPriceFeatures: PropTypes.shape({
    msrpConfig: PropTypes.shape({}),
    trimNames: PropTypes.arrayOf(PropTypes.string),
    vehicleInfo: PropTypes.shape({}),
  }),
  ads12450Recipe: PropTypes.string,
  ads12859Recipe: PropTypes.string,
};

export function MultiCtaAd({
  ads12450Recipe = null,
  ads11455Ads11617Recipe = null,
  mobile = true,
  ads12859Recipe = null,
}) {
  const [offersHeadline, setOffersHeadline] = useState();
  const [headline2, setHeadline2] = useState();
  const [jellybeanImage, setJellybeanImage] = useState({});
  const [buildPriceFeatures, setBuildPriceFeatures] = useState({});
  const [logo, setLogo] = useState();
  const [isConquest, seIsConquest] = useState();
  const [linkDisplayUrl, setLinkDisplayUrl] = useState();
  const onIncentivesAdLoadedCallback = useCallback(obj => {
    setOffersHeadline(obj.headline);
    setHeadline2(obj.headline2);
  }, []);
  const onBuildPriceAdLoadedCallback = useCallback(obj => {
    setJellybeanImage(obj.jellybeanImage);
    setBuildPriceFeatures(obj.features);
    setLogo(obj.logo);
    seIsConquest(obj.isConquest);
    setLinkDisplayUrl(obj.linkDisplayUrl);
  }, []);

  const isAds12859Enabled = ['chal1', 'chal2'].includes(ads12859Recipe);
  return (
    <div
      className={classnames('d-flex align-items-end multi-cta-ad-cls-wrapper', {
        'mt-1 px-0_5': !isAds12859Enabled,
        'm-0 flex-column': isAds12859Enabled,
      })}
      style={
        isAds12859Enabled ? { minHeight: '351px', backgroundColor: 'white' } : { minHeight: '351px', margin: '0 -8px' }
      }
    >
      {isAds12859Enabled && <hr className="w-100 mt-2 mb-0_5" />}
      <Card
        className={classnames('multi-cta-ad-wrapper d-flex flex-column w-100', {
          'mt-auto no-drop-shadow': isAds12859Enabled,
        })}
        style={{ minHeight: '260px' }}
      >
        <AdLabel />
        {isConquest ? (
          <ConquestRibbon style={{ height: '20px', top: '12px', position: 'absolute' }} />
        ) : (
          <img className="pos-a ps-1" alt="" src={logo} height={'24'} style={{ top: '20px' }} />
        )}
        <MultiCtaAdFeatures
          offersHeadline={offersHeadline}
          headline2={isConquest ? headline2 : null}
          jellybeanImage={jellybeanImage}
          buildPriceFeatures={buildPriceFeatures}
          ads12450Recipe={ads12450Recipe}
          ads12859Recipe={ads12859Recipe}
        />
        <div
          className={classnames(
            'multi-cta-ads-container d-flex justify-content-center justify-content-md-start mt-auto',
            {
              'px-0_5': !isAds12859Enabled,
              'px-0': isAds12859Enabled,
            }
          )}
          style={{ gap: '0.5rem' }}
        >
          <IncentivesAd
            onIncentivesAdLoadedCallback={onIncentivesAdLoadedCallback}
            ads12450Recipe={ads12450Recipe}
            ads12859Recipe={ads12859Recipe}
            ads11455Ads11617Recipe={ads11455Ads11617Recipe}
            mobile
          />
          <BuildPriceAd
            includeMrectAdCall={false}
            onBuildPriceAdLoadedCallback={onBuildPriceAdLoadedCallback}
            ads12450Recipe={ads12450Recipe}
            ads12859Recipe={ads12859Recipe}
            mobile={mobile}
          />
          <PricingAd ads12450Recipe={ads12450Recipe} ads12859Recipe={ads12859Recipe} mobile />
        </div>
        <div
          className="d-flex justify-content-center align-items-center medium text-blue-20 my-0_5"
          style={{ height: '20px' }}
        >
          {linkDisplayUrl}
        </div>
      </Card>
    </div>
  );
}

MultiCtaAd.propTypes = {
  ads12450Recipe: PropTypes.string,
  ads12859Recipe: PropTypes.string,
  ads11455Ads11617Recipe: PropTypes.string,
  mobile: PropTypes.bool,
};
